/* eslint-disable @typescript-eslint/no-explicit-any */
import * as H from 'history';
import { LocalDate, LocalDateTime, ZoneId } from '@js-joda/core';
import { core } from './index';
import { defaultMemoize } from 'reselect';
import React, { Component, PureComponent, createContext, useCallback, useContext } from 'react';
import _ from 'lodash';

export interface Context {
  // activeBusiness is only set in Dashboard
  activeBusiness?: core.Business;
  getActiveBusinessCurrentDate: () => LocalDate | undefined;
  getActiveBusinessCurrentDateTime: () => LocalDateTime | undefined;
  // appContext is only set on Consumer
  appContext?: { pathPrefix: string };
  currency: string;
  isFeatureEnabled(featureName: core.Feature.FeatureName): boolean;
  isFeatureEnabled(featureName: core.Feature.FeatureName): boolean;
  // isFlexBusiness should only be used in Dashboard
  isFlexBusiness(): boolean;
  // isEventsBusiness should only be used in Dashboard
  isEventsBusiness(): boolean;
  // isScheduledInventoryConfig should only be used in Dashboard
  isScheduledInventoryConfig(): boolean;
  // shouldShowHiddenFlexFeatures should only be used in Dashboard
  shouldShowHiddenFlexFeatures(): boolean;
  locale: string;
  timeZone: string;
}

export type Features = { [P in core.Feature.FeatureName]?: boolean };

enum RoleType {
  // Internal role types
  ENG,
  HOSPITALITY,
  FINANCE,
}

export function hasRole(activeAuth: any, roleTypes: RoleType[]): boolean {
  return !!activeAuth && _.includes(roleTypes, activeAuth.roleType);
}

export const AppContext = createContext<MyContext>({
  activeBusiness: undefined,
  getActiveBusinessCurrentDate: () => undefined,
  getActiveBusinessCurrentDateTime: () => undefined,
  appContext: null as any,
  currency: 'USD',
  history: null as any,
  isFeatureEnabled: () => false,
  isFlexBusiness: () => false,
  isEventsBusiness: () => false,
  isScheduledInventoryConfig: () => false,
  shouldShowHiddenFlexFeatures: () => false,
  locale: 'en-US',
  timeZone: 'US/Central',
});

export const CurrencyContext = createContext<string>('USD');
export const LocaleContext = createContext<string>('en-US');
export const TimeZoneContext = createContext<string>('US/Central');
export const MoneyFilterContext = createContext<
  ({ allowNegative, decimalScale }?: CreateMoneyFilterOptions) => InputFilterStrict<number>
>(undefined as any);

export interface CreateMoneyFilterOptions {
  /**
   * defaults to false
   */
  allowNegative?: boolean;
  /**
   * number of decimal places, defaults to 2
   */
  decimalScale?: 2 | 0;
}

/**
 * Does a render that consumes currency, locale, and timezone from within a DisplayFormatsProvider.
 *
 * The provided child is a render prop conforming to the typing shown below.
 */
export function DisplayFormatsConsumer({
  children,
}: {
  children: (currency: string, locale: string, timeZone: string) => React.ReactNode;
}) {
  return (
    <CurrencyContext.Consumer>
      {(currency: string) => (
        <LocaleContext.Consumer>
          {(locale: string) => (
            <TimeZoneContext.Consumer>
              {(timeZone: string) => {
                if (!currency || !locale || !timeZone) {
                  throw new Error('DisplayFormatsConsumer must be used within a Provider');
                }
                return children(currency, locale, timeZone);
              }}
            </TimeZoneContext.Consumer>
          )}
        </LocaleContext.Consumer>
      )}
    </CurrencyContext.Consumer>
  );
}

/** Similar to `InputFilter`, defined in shared/typings/index.d.ts, except strict with conversion of type<>input string */
export interface InputFilterStrict<T> {
  format(modelValue: T): string;
  parse(viewValue: string): T;
  formatOnChange: boolean;
}

export interface Props {
  activeBusiness?: core.Business;
  activeAuth?: any;
  appContext?: any;
  children?: React.ReactNode;
  currency: string;
  createMoneyFilterCreator: (
    currency: string
  ) => (options?: CreateMoneyFilterOptions) => InputFilterStrict<number>;
  features: Features;
  history?: H.History;
  locale: string;
  timeZone: string;
  isChase?: boolean;
}

type MyContext = Context & {
  history: H.History;
};

// We need to call the property passed in something other than `ref` because
// react swallows an attempt to pass `ref` on to the child component.
// See https://facebook.github.io/react/warnings/special-props.html for more info
type RefFunction = (node: React.ReactNode) => void;
export interface IWithContextRef {
  inputRef?: string | RefFunction;
}

export class AppContextProviderWithoutMoneyAwareness extends PureComponent<Props> {
  isFeatureEnabled = (feature: core.Feature.FeatureName): boolean => {
    const { features } = this.props;
    return features[feature] || false;
  };

  isFlexBusiness = (): boolean => {
    const { activeBusiness } = this.props;
    if (!activeBusiness) {
      return false;
    }

    return activeBusiness.inventoryMode === 'DEFAULT_TO_FLEX';
  };

  isEventsBusiness = (): boolean => {
    const { activeBusiness } = this.props;
    if (!activeBusiness) {
      return false;
    }

    return activeBusiness.tockProductType === 'EVENTS';
  };

  isScheduledInventoryConfig = (): boolean => {
    const { activeBusiness } = this.props;
    return !!(activeBusiness && activeBusiness.scheduledInventoryConfig);
  };

  shouldShowHiddenFlexFeatures = (): boolean => {
    return this.isFeatureEnabled('DISPLAY_FEATURES_HIDDEN_IN_FLEX');
  };

  getActiveBusinessCurrentDate() {
    const { activeBusiness } = this.props;
    if (!activeBusiness) {
      return undefined;
    }

    const localTimeZone = ZoneId.of(activeBusiness.timeZone);
    return LocalDate.now(localTimeZone);
  }

  getActiveBusinesCurentDateTime() {
    const { activeBusiness } = this.props;
    if (!activeBusiness) {
      return undefined;
    }

    const localTimeZone = ZoneId.of(activeBusiness.timeZone);
    return LocalDateTime.now(localTimeZone);
  }

  getContext = defaultMemoize(
    (props: Props): MyContext => ({
      activeBusiness: props.activeBusiness,
      getActiveBusinessCurrentDate: () => this.getActiveBusinessCurrentDate(),
      getActiveBusinessCurrentDateTime: () => this.getActiveBusinesCurentDateTime(),
      appContext: props.appContext,
      currency: props.currency,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      history: props.history!,
      locale: props.locale,
      timeZone: props.timeZone,
      isFeatureEnabled: this.isFeatureEnabled,
      isFlexBusiness: this.isFlexBusiness,
      isEventsBusiness: this.isEventsBusiness,
      isScheduledInventoryConfig: this.isScheduledInventoryConfig,
      shouldShowHiddenFlexFeatures: this.shouldShowHiddenFlexFeatures,
    })
  );

  render() {
    const { children, createMoneyFilterCreator, currency, locale, timeZone } = this.props;
    const moneyFilterCreator = createMoneyFilterCreator(currency);
    return (
      <CurrencyContext.Provider value={currency}>
        <LocaleContext.Provider value={locale}>
          <TimeZoneContext.Provider value={timeZone}>
            <MoneyFilterContext.Provider value={moneyFilterCreator}>
              <AppContext.Provider value={this.getContext(this.props)}>
                {children}
              </AppContext.Provider>
            </MoneyFilterContext.Provider>
          </TimeZoneContext.Provider>
        </LocaleContext.Provider>
      </CurrencyContext.Provider>
    );
  }
}

type GetPath<T extends H.LocationDescriptor = H.Path> = (route: T) => T;

export interface IWithContextProps {
  context: Context;
  routeTo(route: H.LocationDescriptor): void;
  replaceRouteWith(route: H.LocationDescriptor): void;
  getPath: GetPath;
}

const getPath = (context: MyContext, route: H.LocationDescriptor) => {
  const prefix: string = context?.appContext?.pathPrefix ?? '';
  if (_.isString(route)) {
    const addPrefix = !_.startsWith(route, prefix);
    return `${addPrefix ? prefix : ''}${route}`;
  }

  const addPrefix = !_.startsWith(route.pathname, prefix);
  return {
    ...route,
    pathname: `${addPrefix ? prefix : ''}${route.pathname}`,
  };
};

type ClassAttributes = React.ClassAttributes<HTMLElement>;

export function withContext<P>(
  WrappedComponent: React.ComponentType<
    React.PropsWithChildren<P & IWithContextProps & ClassAttributes>
  >
): React.ComponentClass<P & IWithContextRef> {
  class WithAppContext extends Component<P & IWithContextRef> {
    static displayName: string;
    routeTo = defaultMemoize((context: MyContext) => (route: H.LocationDescriptor) => {
      const path = getPath(context, route);
      // I know this is weird. this history method has two overloads, and the two
      // types that path can be fit each of them, respectively. Thus, to make TS
      // happy, we must branch here. CM 1/20/20
      if (_.isString(path)) {
        context.history.push(path);
      } else {
        context.history.push(path);
      }
    });

    replaceRouteWith = defaultMemoize((context: MyContext) => (route: H.LocationDescriptor) => {
      const path = getPath(context, route);
      // See comment above
      if (_.isString(path)) {
        context.history.replace(path);
      } else {
        context.history.replace(path);
      }
    });

    getPath = defaultMemoize((context: MyContext): any => getPath.bind(this, context));

    // The any below is a result of fixing our react / emotion types.
    // I have not figured out how to eliminate it. See https://github.com/emotion-js/emotion/issues/2169
    renderComponent = (context: MyContext, props: P) => {
      return (
        <WrappedComponent
          context={context}
          {...(props as any)}
          routeTo={this.routeTo(context)}
          replaceRouteWith={this.replaceRouteWith(context)}
          getPath={this.getPath(context)}
          ref={this.props.inputRef}
        />
      );
    };

    render() {
      return (
        <AppContext.Consumer>
          {(context: MyContext) => this.renderComponent(context, this.props)}
        </AppContext.Consumer>
      );
    }
  }

  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  WithAppContext.displayName = `WithAppContext(${wrappedComponentName})`;

  return WithAppContext;
}

export const useCurrencyContext = () => {
  return useContext(CurrencyContext);
};

export const useAppContext = () => {
  const context: MyContext = useContext(AppContext);
  const routeTo = defaultMemoize((route: H.LocationDescriptor) => {
    const path = getPath(context, route);
    // I know this is weird. this history method has two overloads, and the two
    // types that path can be fit each of them, respectively. Thus, to make TS
    // happy, we must branch here. CM 1/20/20
    if (_.isString(path)) {
      context.history.push(path);
    } else {
      context.history.push(path);
    }
  });

  const replaceRouteWith = defaultMemoize((route: H.LocationDescriptor) => {
    const path = getPath(context, route);
    // See comment above
    if (_.isString(path)) {
      context.history.replace(path);
    } else {
      context.history.replace(path);
    }
  });

  const contextGetPath = useCallback(
    (route: H.LocationDescriptor) => {
      return getPath(context, route);
    },
    [context]
  );

  return {
    routeTo,
    replaceRouteWith,
    context,
    getPath: contextGetPath,
  };
};
